import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { ArrowDownTrayIcon, ArrowPathIcon, ArrowTopRightOnSquareIcon, ExclamationTriangleIcon, XMarkIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import AnimatedModal from '../ui/AnimatedModal';
import ScoreParameterCard from './ScoreParameterCard';
import CriterionResultCard from './CriterionResultCard';
import { isResumeExtractionFailed } from '../../pages/screenings/resumeCellState';
import { getResumeViewUrl, getPublicResumeViewUrl } from '../../utils/resumeViewUrl';
import { getCandidateDisplayName } from '../../utils/candidateName';
import { API_BASE_URL } from '../../lib/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';

function getWorkExperienceArray(candidate) {
  const fromSrt = candidate?.structuredResumeText?.workExperience;
  if (Array.isArray(fromSrt) && fromSrt.length > 0) return fromSrt;
  const top = candidate?.workExperience;
  if (Array.isArray(top) && top.length > 0) return top;
  return [];
}

/**
 * When `previousCompanies` is not populated on the document, derive from structured resume
 * work experience (most recent role first; exclude current company).
 */
function getPreviousCompaniesFromWorkExperience(candidate) {
  const we = getWorkExperienceArray(candidate);
  if (!Array.isArray(we) || we.length < 2) return [];
  const names = we
    .map((w) => (w && typeof w.companyName === 'string' ? w.companyName.trim() : ''))
    .filter(Boolean);
  if (names.length < 2) return [];
  const current = typeof candidate?.company === 'string' ? candidate.company.trim() : '';
  if (!current) {
    return names.slice(1);
  }
  const curLower = current.toLowerCase();
  const idx = names.findIndex((n) => n.toLowerCase() === curLower);
  if (idx === 0) {
    return names.slice(1);
  }
  if (idx > 0) {
    return names.filter((_, i) => i !== idx);
  }
  return names.slice(1);
}

const parseCompensationToLpa = (value) => {
  if (value == null) return null;
  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }
  const raw = String(value).trim();
  if (!raw) return null;
  const numeric = parseFloat(raw.replace(/[^0-9.]/g, ''));
  if (Number.isNaN(numeric)) return null;
  // Heuristic: values >= 1000 are likely rupees and should be converted to lacs.
  return numeric >= 1000 ? Math.round((numeric / 100000) * 100) / 100 : numeric;
};

/**
 * Normalizes candidate from either job-results (score) or screening (resumeScore) shape
 * for consistent display in the modal.
 */
function normalizeCandidate(candidate) {
  if (!candidate) return null;
  const score = candidate.score ?? candidate.resumeScore ?? null;

  const eduList =
    candidate.structuredResumeText &&
    Array.isArray(candidate.structuredResumeText.education) &&
    candidate.structuredResumeText.education.length > 0
      ? candidate.structuredResumeText.education
      : [];

  const isGroundedEducationEntry = (edu) => {
    if (!edu || typeof edu !== 'object') return false;
    const degree = typeof edu.degree === 'string' ? edu.degree.trim() : '';
    if (!degree) return false;
    const major = typeof edu.major === 'string' ? edu.major.trim() : '';
    const institution = typeof edu.institution === 'string' ? edu.institution.trim() : '';
    const graduationDate =
      typeof edu.graduationDate === 'string' ? edu.graduationDate.trim() : '';
    // Avoid displaying "degree-only" entries which are frequently over-inferred by the parser.
    return Boolean(major || institution || graduationDate);
  };

  const formatEducationEntry = (edu) => {
    if (!edu || typeof edu !== 'object') return '';
    const degree = typeof edu.degree === 'string' ? edu.degree.trim() : '';
    const major = typeof edu.major === 'string' ? edu.major.trim() : '';
    const institution = typeof edu.institution === 'string' ? edu.institution.trim() : '';
    if (!degree) return '';
    const degreeWithMajor = major ? `${degree} in ${major}` : degree;
    // Keep this compact in the grid; institution is useful but optional.
    return institution ? `${degreeWithMajor}, ${institution}` : degreeWithMajor;
  };

  const getEducationRank = (edu) => {
    if (!edu || typeof edu !== 'object') return 0;
    const normalized = String(edu.normalizedLevelOfDegree || '').toUpperCase();
    if (normalized === 'DOCTORATE') return 5;
    if (normalized === 'MASTERS') return 4;
    if (normalized === 'BACHELORS') return 3;
    if (normalized === 'DIPLOMA' || normalized === 'CERTIFICATION') return 2;
    const degree = String(edu.degree || '').toLowerCase();
    if (/\b(phd|doctorate|doctoral)\b/.test(degree)) return 5;
    if (/\b(master(?:'s|s)?|m\.?\s*tech|mtech|m\.?\s*sc|msc|mba)\b/.test(degree)) return 4;
    if (/\b(bachelor(?:'s|s)?|b\.?\s*tech|btech|b\.?\s*e\.?|bca|bsc)\b/.test(degree)) return 3;
    return 1;
  };

  // Prefer grounded entries, else best degree-only entry by education rank.
  const groundedEdu = eduList.find(isGroundedEducationEntry) || null;
  const bestDegreeOnlyEdu = eduList
    .filter((edu) => edu && typeof edu === 'object' && String(edu.degree || '').trim())
    .sort((a, b) => getEducationRank(b) - getEducationRank(a))[0] || null;
  const educationFromSrt = groundedEdu
    ? formatEducationEntry(groundedEdu)
    : bestDegreeOnlyEdu
      ? formatEducationEntry(bestDegreeOnlyEdu)
      : null;

  const normalizedCandidateEducation =
    typeof candidate.education === 'string' ? candidate.education.trim() : '';
  const educationFromCandidate =
    normalizedCandidateEducation &&
    // If it's just a bare degree string without any major/institution context, treat as unreliable.
    /bachelor|b\.?\s?e\b|b\.?\s?tech|btech|bca|bsc/i.test(normalizedCandidateEducation) &&
    !/,/.test(normalizedCandidateEducation)
      ? ''
      : /^(associates|bachelors|masters|doctorates|other)$/i.test(normalizedCandidateEducation)
        ? ''
      : normalizedCandidateEducation;

  const maxEducationLevelFromCandidate =
    typeof candidate.maxEducationLevel === 'string' ? candidate.maxEducationLevel.trim() : '';
  const maxEducationLevelFromSrt =
    candidate.structuredResumeText && typeof candidate.structuredResumeText.maxEducationLevel === 'string'
      ? candidate.structuredResumeText.maxEducationLevel.trim()
      : '';
  const maxEducationLevel = maxEducationLevelFromCandidate || maxEducationLevelFromSrt || '';
  const p1Detected = candidate.parameters?.p1_education?.detected || null;
  const p1HighestEducationLevel =
    p1Detected && typeof p1Detected.highestEducationLevel === 'string'
      ? p1Detected.highestEducationLevel.trim()
      : '';
  const p1Field =
    p1Detected && typeof p1Detected.field === 'string' ? p1Detected.field.trim() : '';
  const p1FieldIsSpecific = p1Field && !/^(unclear|other)$/i.test(p1Field);
  const p1HighestIsValid = p1HighestEducationLevel && !/^(none|n\/a|null|undefined)$/i.test(p1HighestEducationLevel);
  const educationFromP1 =
    p1HighestIsValid && p1FieldIsSpecific
      ? `${p1HighestEducationLevel} in ${p1Field}`
      : p1HighestIsValid
        ? p1HighestEducationLevel
        : '';

  const availabilityFromSrt =
    candidate.structuredResumeText && candidate.structuredResumeText.availabilityAndCompensation
      ? candidate.structuredResumeText.availabilityAndCompensation
      : null;

  const noticePeriodDays =
    availabilityFromSrt && typeof availabilityFromSrt.noticePeriodDays === 'number'
      ? availabilityFromSrt.noticePeriodDays
      : null;

  const currentCtcLacsFromSrt =
    availabilityFromSrt && typeof availabilityFromSrt.currentCtcLacs === 'number'
      ? availabilityFromSrt.currentCtcLacs
      : null;

  const expectedCtcLacsFromSrt =
    availabilityFromSrt && typeof availabilityFromSrt.expectedCtcLacs === 'number'
      ? availabilityFromSrt.expectedCtcLacs
      : null;
  const expectedCtcParsedFromSalaryExpectation = parseCompensationToLpa(candidate.salaryExpectation);

  const scoringVersion = candidate.scoringVersion || 'v1.5';
  const isV2 = scoringVersion.startsWith('v2');

  const education = educationFromCandidate || educationFromSrt || educationFromP1 || maxEducationLevel || '';

  return {
    ...candidate,
    education,
    score,
    scoreBreakdown: candidate.scoreBreakdown || {},
    availability: {
      noticePeriodDays,
      currentCtcLacs: currentCtcLacsFromSrt ?? candidate.currentCtcLacs ?? null,
      expectedCtcLacs:
        expectedCtcLacsFromSrt ??
        candidate.expectedCtcLacs ??
        (typeof candidate.salaryExpectation === 'number'
          ? candidate.salaryExpectation
          : expectedCtcParsedFromSalaryExpectation),
    },
    scoringVersion,
    isV2,
    criteriaResults: candidate.criteriaResults || [],
    scoringSummary: candidate.scoringSummary || '',
    dealBreakerConcerns: candidate.dealBreakerConcerns || [],
    hasDealBreakerConcerns: candidate.hasDealBreakerConcerns || false,
    // Resume was uploaded but text extraction failed entirely (bad/empty/unreadable file) —
    // drives the "could not be read" banner in the score tab.
    isResumeExtractionFailed: isResumeExtractionFailed(candidate),
  };
}

/**
 * Renders a single field: editable (with onSaveField) or read-only.
 */
function FieldRenderer({
  fieldName,
  value,
  editValue,
  placeholder = 'N/A',
  type = 'text',
  editable,
  editingField,
  editingValue,
  updating,
  onStartEdit,
  onSave,
  onCancel,
  onEditingValueChange,
}) {
  const displayValue = value ?? '';
  const isEditing = editable && editingField === fieldName;
  const isUrlField =
    fieldName === 'linkedinUrl' ||
    fieldName === 'githubUrl' ||
    fieldName === 'portfolioUrl' ||
    fieldName === 'behanceUrl' ||
    fieldName === 'websiteUrl';

  if (isEditing) {
    const isTel = type === 'tel';
    const isNumber = type === 'number';
    return (
      <div className="flex items-center gap-1 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-200">
        <input
          type={type}
          value={editingValue}
          inputMode={isTel || isNumber ? 'numeric' : undefined}
          pattern={isTel || isNumber ? '[0-9]*' : undefined}
          onChange={(e) => {
            const next = e.target.value;
            if (isTel) return onEditingValueChange(String(next).replace(/[^0-9]/g, ''));
            if (isNumber) return onEditingValueChange(String(next).replace(/[^0-9.]/g, ''));
            return onEditingValueChange(next);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSave(fieldName);
            else if (e.key === 'Escape') onCancel();
          }}
          onBlur={() => onSave(fieldName)}
          autoFocus
          className="flex-1 px-1.5 py-0.5 text-xs border border-indigo-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
          disabled={updating}
        />
        <button
          type="button"
          onClick={() => onSave(fieldName)}
          disabled={updating}
          className="px-1.5 py-0.5 text-xs font-medium bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
          title="Save (Enter)"
        >
          ✓
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={updating}
          className="px-1.5 py-0.5 text-xs font-medium bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 transition-colors"
          title="Cancel (Esc)"
        >
          ✕
        </button>
      </div>
    );
  }

  if (editable) {
    const rawUrl = typeof displayValue === 'string' ? displayValue : '';
    const urlForOpen = rawUrl ? rawUrl.replace(/\s+/g, '') : '';
    const canOpen = isUrlField && Boolean(urlForOpen);
    const valToEdit = editValue !== undefined ? editValue : displayValue;
    return (
      <div
        className="flex items-center justify-between gap-2 text-xs text-gray-700 group transition-colors px-1.5 py-0.5 rounded hover:bg-indigo-50"
        onKeyDown={(e) => e.key === 'Enter' && onStartEdit(fieldName, valToEdit)}
        role="button"
        tabIndex={0}
        title="Double click to edit"
      >
        <div
          className="min-w-0 flex-1 cursor-pointer hover:text-indigo-600"
          onDoubleClick={() => onStartEdit(fieldName, valToEdit)}
        >
          <span className={`break-words ${displayValue ? 'font-medium' : 'text-gray-400 italic font-normal'}`}>
            {displayValue || placeholder}
          </span>
        </div>

        {isUrlField ? (
          <button
            type="button"
            className={`shrink-0 rounded p-1 transition-colors ${
              canOpen ? 'text-gray-500 hover:text-indigo-600 hover:bg-white' : 'text-gray-300 cursor-not-allowed'
            }`}
            title={canOpen ? 'Open link in new tab' : 'No link to open'}
            disabled={!canOpen}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!urlForOpen) return;
              window.open(urlForOpen, '_blank', 'noopener,noreferrer');
            }}
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <span
      className={`text-xs text-gray-700 ${displayValue ? 'font-medium' : 'text-gray-400 italic'}`}
    >
      {displayValue || placeholder}
    </span>
  );
}

/**
 * CandidateInsightModal – resume viewer + score breakdown + candidate details.
 * Reusable from JobResults (editable) and ScreeningDetailPage (read-only or editable via onSaveField).
 *
 * @param {boolean} isOpen
 * @param {() => void} onClose
 * @param {Object} candidate – raw candidate (score or resumeScore, scoreBreakdown, resumeUrl, etc.)
 * @param {'score'|'resume'|'candidate'} initialTab – tab to show when opened
 * @param {(candidateId: string, updateData: Object) => Promise<void>} [onSaveField] – if provided, details are editable and this is called on save
 * @param {boolean} [candidateDetailsOnly] – when true, only candidate details are shown (no Score / View Resumes tabs). Used e.g. screening “Candidate details” column.
 */
function CandidateInsightModal({
  isOpen,
  onClose,
  candidate,
  initialTab = 'resume',
  onSaveField,
  candidateDetailsOnly = false,
  detailsLoading = false,
  // Public / hiring-manager mode
  publicMode = false,
  /** Required for public resume preview/download (same server path as recruiter download-resume). */
  publicShareCode = '',
  /** Screening this candidate belongs to. Required to expose the admin resume-rescore action. */
  screeningId = '',
  /** Called with the candidate id after a successful rescore so the parent can refetch. */
  onRescored,
}) {
  const { user } = useAuth();
  const SkeletonBlock = ({ className = '' }) => (
    <div className={`relative overflow-hidden rounded-md bg-gray-200/90 ${className}`}>
      <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/60 to-transparent" />
    </div>
  );

  const [tab, setTab] = useState(initialTab);
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [editingOriginalValue, setEditingOriginalValue] = useState('');
  const [updating, setUpdating] = useState(false);
  const [draftEdits, setDraftEdits] = useState({});
const [downloadingResume, setDownloadingResume] = useState(false);
const [resumeLoading, setResumeLoading] = useState(false);
const [displayResumeUrl, setDisplayResumeUrl] = useState(null);
const [resumeIsPdf, setResumeIsPdf] = useState(true); // false = show download UI instead of inline viewer
const [aiSummaryExpanded, setAiSummaryExpanded] = useState(false);
const [showRescoreButton, setShowRescoreButton] = useState(false);
const [rescoringResume, setRescoringResume] = useState(false);

useEffect(() => {
  if (isOpen && candidate) setTab(initialTab);
}, [isOpen, candidate?._id]);

useEffect(() => {
  if (isOpen && candidateDetailsOnly) setTab('candidate');
}, [isOpen, candidateDetailsOnly]);
  useEffect(() => {
    if (!isOpen) {
      setDraftEdits({});
      return;
    }
    // New session: reset draft to avoid leaking edits across candidates.
    setDraftEdits({});
  }, [isOpen, candidate?._id]);

  const formatAiSummaryParagraphs = useCallback((text) => {
    const raw = typeof text === 'string' ? text : '';
    const s = raw.replace(/\r\n/g, '\n').trim();
    if (!s) return [];

    // Respect explicit paragraph breaks if present.
    const hasParagraphBreaks = /\n\s*\n/.test(s);
    const blocks = hasParagraphBreaks ? s.split(/\n\s*\n/g) : [s];
    const flattened = blocks
      .flatMap((b) => b.split('\n'))
      .map((p) => p.trim())
      .filter(Boolean)
      .map((p) => p.replace(/^[-*•]+\s*/, ''))
      .map((p) => p.replace(/\s+/g, ' ').trim())
      .filter(Boolean);

    // If it's one big paragraph, split into sentence-based mini-paragraphs (2 sentences each).
    // Uses a protect-split-restore strategy to avoid breaking on abbreviations, decimals, and
    // technology names (e.g. Node.js, B.Tech, 3.5 years, Sr. Engineer).
    if (!hasParagraphBreaks && flattened.length === 1) {
      const one = flattened[0];
      const DOT = '__DOT__';

      // 1. Protect periods that are NOT sentence endings.
      let safe = one;
      // Decimal numbers: 3.5, 10.2
      safe = safe.replace(/(\d)\.(\d)/g, `$1${DOT}$2`);
      // Tech extensions: Node.js, Vue.js, D3.js, ASP.net, etc.
      safe = safe.replace(/(\w)\.([a-zA-Z]{1,4})\b/g, `$1${DOT}$2`);
      // Common abbreviations: Sr., Jr., Dr., Mr., Mrs., Ms., Prof., Inc., Ltd., Corp., etc.
      safe = safe.replace(/\b(Mr|Mrs|Ms|Dr|Sr|Jr|Prof|Inc|Ltd|Corp|Dept|Govt|Assoc|Mgmt|approx|vol)\./gi, `$1${DOT}`);
      // Latin abbreviations: e.g., i.e., vs., etc.
      safe = safe.replace(/\b(e\.g|i\.e|vs|etc)\./gi, (m) => m.replace(/\./g, DOT));
      // Single-letter abbreviations (covers B.Tech, M.Sc., Ph.D., A.I., etc.)
      safe = safe.replace(/\b([A-Z])\./g, `$1${DOT}`);

      // 2. Split on sentence-ending punctuation followed by whitespace.
      const sentences = safe
        .split(/(?<=[.!?])\s+/)
        .map((t) => t.replace(new RegExp(DOT.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), '.').trim())
        .filter(Boolean);

      // 3. Group into pairs of 2 sentences per paragraph for readability.
      const grouped = [];
      for (let i = 0; i < sentences.length; i += 2) {
        grouped.push([sentences[i], sentences[i + 1]].filter(Boolean).join(' '));
      }
      return grouped.length > 0 ? grouped : [one];
    }

    return flattened;
  }, []);

  useEffect(() => {
    if (!isOpen) setAiSummaryExpanded(false);
  }, [isOpen]);

  const normalized = normalizeCandidate(candidate);
  // Edit mode is always enabled; persistence happens on modal close (requires onSaveField).
  const editable = true;

  const canonicalDisplayName =
    typeof candidate?.canonicalDisplayName === 'string' ? candidate.canonicalDisplayName.trim() : '';
  const displayName = canonicalDisplayName || getCandidateDisplayName(normalized) || '';

  const canonicalizeDraftValue = useCallback((fieldName, rawValue) => {
    // Normalize input in the same way we'll send to backend, so diffing is accurate.
    if (fieldName === 'age' || fieldName === 'ageYears') {
      const raw = String(rawValue ?? '').trim();
      const parsed = raw ? parseInt(raw, 10) : NaN;
      return Number.isNaN(parsed) ? null : parsed;
    }
    if (fieldName === 'experienceYears') {
      const raw = String(rawValue ?? '').trim();
      const parsed = raw ? parseFloat(raw) : NaN;
      return Number.isNaN(parsed) ? null : parsed;
    }
    if (fieldName === 'currentCtcLacs' || fieldName === 'expectedCtcLacs') {
      const raw = String(rawValue ?? '').trim();
      const parsed = raw ? parseFloat(raw) : NaN;
      return Number.isNaN(parsed) ? null : parsed;
    }
    if (fieldName === 'phone') {
      const digits = String(rawValue ?? '').replace(/[^0-9]/g, '');
      return digits ? digits : null;
    }
    if (
      fieldName === 'linkedinUrl' ||
      fieldName === 'githubUrl' ||
      fieldName === 'portfolioUrl' ||
      fieldName === 'behanceUrl' ||
      fieldName === 'websiteUrl'
    ) {
      const compact = String(rawValue ?? '').replace(/\s+/g, '');
      if (!compact) return null;
      return /^https?:\/\//i.test(compact) ? compact : `https://${compact}`;
    }
    if (rawValue == null) return null;
    if (typeof rawValue === 'string') {
      const t = rawValue.trim();
      return t ? t : null;
    }
    return rawValue;
  }, []);

  const handleStartEdit = useCallback((fieldName, currentValue) => {
    setEditingField(fieldName);
    const existing = Object.prototype.hasOwnProperty.call(draftEdits, fieldName)
      ? draftEdits[fieldName]
      : currentValue;
    setEditingValue(existing ?? '');
    setEditingOriginalValue(currentValue ?? '');
  }, [draftEdits]);

  const handleCancelEdit = useCallback(() => {
    setEditingField(null);
    setEditingValue('');
    setEditingOriginalValue('');
  }, []);

  const handleSaveField = useCallback(
    (fieldName) => {
      // Commit into local draft state; backend save happens on modal close.
      const nextCanonical = canonicalizeDraftValue(fieldName, editingValue);
      const originalCanonical = canonicalizeDraftValue(fieldName, editingOriginalValue);

      setDraftEdits((prev) => {
        // If value didn't actually change, don't keep it in draft.
        if (nextCanonical === originalCanonical) {
          if (!Object.prototype.hasOwnProperty.call(prev, fieldName)) return prev;
          const { [fieldName]: _removed, ...rest } = prev;
          return rest;
        }
        return { ...prev, [fieldName]: editingValue };
      });
      setEditingField(null);
      setEditingValue('');
      setEditingOriginalValue('');
    },
    [editingValue, editingOriginalValue, canonicalizeDraftValue]
  );

  const buildUpdateDataFromDraft = useCallback((draft) => {
    const out = {};
    for (const [k, v] of Object.entries(draft || {})) {
      const canonical = canonicalizeDraftValue(k, v);
      if (k === 'age' || k === 'ageYears') out.ageYears = canonical;
      else out[k] = canonical;
    }
    return out;
  }, [canonicalizeDraftValue]);

  const handleClose = useCallback(async () => {
    // If we have pending edits and a save handler, attempt to persist first.
    // On validation error, keep modal open and preserve draft values.
    if (normalized?._id && onSaveField && draftEdits && Object.keys(draftEdits).length > 0) {
      try {
        setUpdating(true);
        const updateData = buildUpdateDataFromDraft(draftEdits);

        // Filter out no-op updates (e.g. user opened editor but didn't change value).
        const effectiveUpdateData = {};
        for (const [k, v] of Object.entries(updateData)) {
          const currentVal = canonicalizeDraftValue(k, normalized?.[k]);
          const nextVal = canonicalizeDraftValue(k, v);
          if (currentVal !== nextVal) effectiveUpdateData[k] = v;
        }

        if (Object.keys(effectiveUpdateData).length > 0) {
          await onSaveField(normalized._id, effectiveUpdateData);
          toast.success('Saved changes');
        }

        // Only reset state when we're actually closing.
        setDraftEdits({});
        setTab(initialTab);
        setEditingField(null);
        setEditingValue('');
        setEditingOriginalValue('');
        onClose();
      } catch (err) {
        console.error('Error saving draft edits:', err);
        toast.error(err.response?.data?.message || 'Failed to save changes');
        // Do NOT close; user can correct and try again.
      } finally {
        setUpdating(false);
      }
      return;
    }

    // No pending edits (or no save handler): close immediately.
    setDraftEdits({});
    setTab(initialTab);
    setEditingField(null);
    setEditingValue('');
    setEditingOriginalValue('');
    onClose();
  }, [initialTab, onClose, normalized?._id, onSaveField, draftEdits, buildUpdateDataFromDraft, canonicalizeDraftValue, normalized]);

  const resumeUrl =
    normalized?.resumeUrl && String(normalized.resumeUrl).trim()
      ? String(normalized.resumeUrl)
      : '';

  useEffect(() => {
    if (!isOpen) {
      setResumeLoading(false);
      setDisplayResumeUrl(null);
      return;
    }
    if (tab !== 'resume') {
      setResumeLoading(false);
      setDisplayResumeUrl(null);
      return;
    }
    // Public mode: fetch resume bytes via share link (matches recruiter getResumeViewUrl / download-resume)
    if (publicMode) {
      if (publicShareCode && resumeUrl && normalized?._id) {
        let cancelled = false;
        setResumeLoading(true);
        setDisplayResumeUrl(null);
        getPublicResumeViewUrl(publicShareCode, String(normalized._id))
          .then((result) => {
            if (!cancelled && result) {
              setDisplayResumeUrl(result.url);
              setResumeIsPdf(result.isPdf);
            }
          })
          .catch(() => {
            if (!cancelled) toast.error('Failed to load resume preview');
          })
          .finally(() => {
            if (!cancelled) setResumeLoading(false);
          });
        return () => {
          cancelled = true;
        };
      }
      const presigned = normalized?.presignedResumeUrl || '';
      setDisplayResumeUrl(presigned || null);
      // Only PDFs render inline; infer from the stored resume URL extension so a .doc/.docx
      // presigned link shows the download fallback instead of a broken PDF viewer.
      setResumeIsPdf(/\.pdf(\?|$)/i.test(resumeUrl || presigned || ''));
      setResumeLoading(false);
      return;
    }
    if (!resumeUrl || !normalized?._id) {
      setResumeLoading(false);
      setDisplayResumeUrl(null);
      return;
    }
    let cancelled = false;
    setResumeLoading(true);
    setDisplayResumeUrl(null);
    getResumeViewUrl(normalized._id)
      .then((result) => {
        if (!cancelled && result) {
          setDisplayResumeUrl(result.url);
          setResumeIsPdf(result.isPdf);
        }
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load resume preview');
      })
      .finally(() => {
        if (!cancelled) setResumeLoading(false);
      });
    return () => { cancelled = true; };
  }, [
    isOpen,
    tab,
    resumeUrl,
    normalized?._id,
    publicMode,
    publicShareCode,
    normalized?.presignedResumeUrl,
  ]);

  const handleDownloadResume = useCallback(async () => {
    if (!resumeUrl || downloadingResume || resumeLoading) return;
    const candidateId = normalized?._id;
    if (!candidateId) return;
    const safeBaseName = (displayName || normalized?.name || 'Resume')
      .toString()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '_')
      .slice(0, 80);
    const fallbackFilename = `${safeBaseName || 'Resume'}.pdf`;

    try {
      setDownloadingResume(true);
      const loadingId = toast.loading('Downloading resume…');
      const token = localStorage.getItem('token');
      const publicDownloadUrl =
        publicMode && publicShareCode
          ? `${API_BASE_URL}/api/screenings/public/candidate-review/${encodeURIComponent(
              String(publicShareCode).trim()
            )}/resume/${candidateId}`
          : null;
      const resp = await fetch(
        publicDownloadUrl || `${API_BASE_URL}/api/screenings/candidates/${candidateId}/download-resume`,
        {
        credentials: 'include',
        headers: publicDownloadUrl || !token ? undefined : { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`Failed to download resume (${resp.status})`);
      const contentType = (resp.headers.get('content-type') || '').toLowerCase();
      if (contentType.includes('text/html')) {
        throw new Error('Unexpected HTML response while downloading resume');
      }
      const blob = await resp.blob();
      const disposition = resp.headers.get('content-disposition') || '';
      const dispositionMatch = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(disposition);
      const decodedName = dispositionMatch?.[1] ? decodeURIComponent(dispositionMatch[1]) : '';
      const filename = decodedName.trim() || fallbackFilename;
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
      toast.dismiss(loadingId);
      toast.success('Resume downloaded');
    } catch (err) {
      console.error('Resume download failed:', err);
      toast.error('Failed to download resume');
    } finally {
      window.setTimeout(() => setDownloadingResume(false), 700);
    }
  }, [
    resumeUrl,
    downloadingResume,
    resumeLoading,
    normalized?._id,
    normalized?.name,
    displayName,
    publicMode,
    publicShareCode,
  ]);

  // ---------------------------------------------------------------------------
  // Admin: re-run resume extraction + LLM scoring for THIS candidate only.
  //
  // Hidden behind the same Ctrl+Shift+S gesture that reveals the bulk "Re-run
  // missing AI" button on ScreeningDetailPage, so one shortcut uncovers every
  // manual reprocess control. Recruiters need this when a resume scored 0% due
  // to a provider error (429 / no credits) or a failed text extraction, and
  // rescoring the whole screening would be wasteful.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen || publicMode) return undefined;
    const onKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && (e.code === 'KeyS' || String(e.key).toLowerCase() === 's')) {
        e.preventDefault();
        setShowRescoreButton((prev) => {
          const next = !prev;
          toast(next ? 'Resume rescore button enabled' : 'Resume rescore button disabled');
          return next;
        });
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, publicMode]);

  const candidateId = candidate?._id ? String(candidate._id) : '';

  const canRescoreResume =
    !publicMode && !candidateDetailsOnly && showRescoreButton && !!screeningId && !!candidateId;

  const handleRescoreResume = useCallback(async () => {
    if (!screeningId || !candidateId || rescoringResume) return;
    if (
      !window.confirm(
        'Re-run AI extraction and scoring for this candidate’s resume? This uses your AI credits and can take up to a minute.'
      )
    ) {
      return;
    }
    setRescoringResume(true);
    const toastId = toast.loading('Rescoring resume…');
    try {
      const res = await axios.post(
        `/api/screenings/${screeningId}/candidates/${candidateId}/reprocess-resume`,
        {},
        { timeout: 180000 }
      );
      const score = res.data?.resumeScore;
      toast.success(
        typeof score === 'number' ? `Resume rescored — ${score}% match` : 'Resume rescored successfully',
        { id: toastId }
      );
      if (typeof onRescored === 'function') await onRescored(candidateId);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to rescore resume', { id: toastId });
    } finally {
      setRescoringResume(false);
    }
  }, [screeningId, candidateId, rescoringResume, onRescored]);

  if (!normalized) return null;

  const formatNoticePeriod = (days, fallback) => {
    if (typeof days !== 'number' || Number.isNaN(days)) return fallback || '';
    if (days === 0) return 'Immediate / 0 days';
    if (days % 30 === 0) {
      const months = days / 30;
      return `${months} month${months > 1 ? 's' : ''} (${days} days)`;
    }
    if (days % 7 === 0) {
      const weeks = days / 7;
      return `${weeks} week${weeks > 1 ? 's' : ''} (${days} days)`;
    }
    return `${days} days`;
  };

  const formatCtc = (val, fallback) => {
    if (typeof val !== 'number' || Number.isNaN(val)) return fallback || '';
    return `${val} LPA`;
  };

  const availability = normalized.availability || {};
  const noticePeriodDisplay = formatNoticePeriod(
    availability.noticePeriodDays,
    normalized.noticePeriod
  );
  const currentCtcDisplay = formatCtc(
    availability.currentCtcLacs,
    normalized.currentCtc || normalized.currentCtcLacs
  );
  const expectedCtcDisplay = formatCtc(
    availability.expectedCtcLacs,
    normalized.expectedCtcLacs || normalized.salaryExpectation
  );
  const currentCtcFieldValue = editable
    ? availability.currentCtcLacs ?? normalized.currentCtcLacs ?? ''
    : currentCtcDisplay || 'N/A';
  const expectedCtcFieldValue = editable
    ? availability.expectedCtcLacs ?? normalized.expectedCtcLacs ?? ''
    : expectedCtcDisplay || 'N/A';
  const noticePeriodDisplayValue = (normalized.availability?.noticePeriodDays !== null && normalized.noticePeriod)
    ? `${noticePeriodDisplay} (${normalized.noticePeriod})`
    : noticePeriodDisplay || normalized.noticePeriod || '';
  const noticePeriodEditValue = normalized.noticePeriod ?? '';

  const toArray = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean).map((v) => String(v).trim()).filter(Boolean);
    if (typeof value === 'string') {
      return value
        .split(/[,;|]/)
        .map((v) => v.trim())
        .filter(Boolean);
    }
    return [];
  };

  const skills = toArray(normalized.skills);
  const languages = toArray(normalized.languages);
  const certifications = toArray(normalized.certifications);
  const previousCompaniesFromDb = toArray(normalized.previousCompanies);
  const previousCompanies = previousCompaniesFromDb.length
    ? previousCompaniesFromDb
    : getPreviousCompaniesFromWorkExperience(normalized);
  const industries = toArray(normalized.industries);
  const profileLinks = Array.isArray(normalized.profileLinks) ? normalized.profileLinks : [];
  const knownLinkSet = new Set(
    [
      normalized.linkedinUrl,
      normalized.githubUrl,
      normalized.behanceUrl,
      normalized.websiteUrl,
      normalized.portfolioUrl,
    ]
      .map((u) => String(u || '').trim())
      .filter(Boolean)
  );
  const extraProfileLinks = profileLinks.filter((l) => {
    const url = String(l?.url || '').trim();
    return url && !knownLinkSet.has(url);
  });

  const latestStatusLabel = normalized.rejected ? 'Rejected' : normalized.shortlisted ? 'Selected' : normalized.status || 'Pending';
  const remotePreferenceMap = {
    remote: 'Remote',
    hybrid: 'Hybrid',
    onsite: 'Onsite',
    flexible: 'Flexible',
    not_specified: 'Not specified',
  };
  const workAuthMap = {
    citizen: 'Citizen',
    permanent_resident: 'Permanent resident',
    work_permit: 'Work permit',
    visa_required: 'Visa required',
    not_specified: 'Not specified',
  };
  const remoteWorkPreferenceDisplay = remotePreferenceMap[normalized.remoteWorkPreference] || '';
  const workAuthorizationDisplay = workAuthMap[normalized.workAuthorization] || '';

  const formatMetaDate = (val) => {
    if (!val) return '';
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const renderField = (fieldName, value, placeholder = 'N/A', type = 'text', editValue = undefined) => {
    const effectiveValue = Object.prototype.hasOwnProperty.call(draftEdits, fieldName)
      ? draftEdits[fieldName]
      : value;
    return (
    <FieldRenderer
      fieldName={fieldName}
      value={effectiveValue}
      editValue={Object.prototype.hasOwnProperty.call(draftEdits, fieldName) ? draftEdits[fieldName] : editValue}
      placeholder={placeholder}
      type={type}
      editable={editable}
      editingField={editingField}
      editingValue={editingValue}
      updating={updating}
      onStartEdit={handleStartEdit}
      onSave={handleSaveField}
      onCancel={handleCancelEdit}
      onEditingValueChange={setEditingValue}
    />
    );
  };

  const workExperienceList = getWorkExperienceArray(normalized);
  const burgerKingExperience = workExperienceList.find(
    (exp) => exp.companyName && exp.companyName.toLowerCase().includes('burger king')
  );
  const isBurgerKingClient = user?.orgSlug === 'b-pia1dr';

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={handleClose}
      contentClassName="bg-white rounded-xl overflow-hidden sm:max-w-5xl w-full h-[88vh] max-h-[88vh] min-h-0 flex flex-col"
      zIndex={110}
    >
      <div className="shrink-0 bg-white px-5 py-3 border-b border-gray-200" data-testid="candidate-insight-modal">
        <div className="flex items-center justify-between">
          {candidateDetailsOnly ? (
            <h2 className="text-sm sm:text-base font-semibold text-gray-900">Candidate Details</h2>
          ) : (
            <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 p-1">
              <button
                type="button"
                onClick={() => setTab('score')}
                data-testid="tab-score"
                className={`px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-full ${
                  tab === 'score'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Score
              </button>
              <button
                type="button"
                onClick={() => setTab('candidate')}
                data-testid="tab-candidate-details"
                className={`px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-full ${
                  tab === 'candidate'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Candidate Details
              </button>
              <button
                type="button"
                onClick={() => setTab('resume')}
                data-testid="view-resumes-tab"
                className={`px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-full ${
                  tab === 'resume'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                View Resumes
              </button>
            </div>
          )}
          <div className="flex items-center gap-2">
            {canRescoreResume && (
              <button
                type="button"
                data-testid="rescore-resume-btn"
                onClick={handleRescoreResume}
                disabled={rescoringResume}
                title="Re-run AI extraction + scoring for this candidate's resume only (uses AI credits)"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-full border border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowPathIcon className={`h-4 w-4 ${rescoringResume ? 'animate-spin' : ''}`} />
                {rescoringResume ? 'Rescoring…' : 'Rescore Resume'}
              </button>
            )}
            {!candidateDetailsOnly && tab === 'resume' && resumeUrl && (
              <button
                type="button"
                data-testid="download-resume-btn"
                onClick={handleDownloadResume}
                disabled={downloadingResume || resumeLoading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-full bg-white text-gray-900 shadow-sm hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none disabled:cursor-not-allowed transition-colors"
                title="Download Resume"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                <span>Download Resume</span>
              </button>
            )}
            <button
              type="button"
              onClick={handleClose}
              data-testid="close-insight-modal-btn"
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              aria-label="Close"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white px-5 py-4">
        {isBurgerKingClient && burgerKingExperience && (
          <div className="mb-4 shrink-0 rounded-md bg-amber-50 p-3 border border-amber-200 shadow-sm flex items-start gap-3">
            <span className="text-xl leading-none">🍔</span>
            <div>
              <h3 className="text-sm font-medium text-amber-800">
                Previous Burger King Experience
              </h3>
              <div className="mt-0.5 text-sm text-amber-700">
                This candidate has previously worked for Burger King
                {burgerKingExperience.startDate || burgerKingExperience.endDate ? (
                  <span>
                    {' '}from <span className="font-semibold">{burgerKingExperience.startDate || 'Unknown'}</span> to <span className="font-semibold">{burgerKingExperience.endDate || 'Present'}</span>
                  </span>
                ) : null}.
              </div>
            </div>
          </div>
        )}
        {!candidateDetailsOnly && tab === 'resume' ? (
          <div className="flex min-h-0 flex-1 flex-col">
            {!resumeUrl && (
              <div className="mb-3 text-center">
                <span className="text-xs text-red-600">Resume not available</span>
              </div>
            )}
            <div
              className="relative min-h-[280px] flex-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-gray-50"
            >
              {resumeUrl ? (
                displayResumeUrl ? (
                  resumeIsPdf ? (
                    // PDF — render inline
                    <object
                      data={`${displayResumeUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                      type="application/pdf"
                      className="w-full h-full rounded-lg"
                      style={{ border: 'none' }}
                      key={displayResumeUrl}
                    >
                      <iframe
                        src={`${displayResumeUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                        className="w-full h-full rounded-lg"
                        title="Resume"
                        style={{ border: 'none' }}
                      />
                    </object>
                  ) : (
                    // DOC / DOCX — browsers cannot render Office files inline.
                    // Show a clear download link instead of triggering a silent download loop.
                    <div className="h-full flex flex-col items-center justify-center gap-3 text-sm text-gray-500">
                      <p>This resume is in Word format and cannot be previewed inline.</p>
                      <a
                        href={displayResumeUrl}
                        download
                        className="inline-flex items-center gap-2 rounded-lg bg-[#007BFF] px-4 py-2 text-sm font-medium text-white hover:bg-[#0069d9]"
                      >
                        Download Resume
                      </a>
                    </div>
                  )
                ) : resumeLoading ? null : (
                  <div className="h-full flex items-center justify-center text-sm text-red-600">
                    Could not load resume. You can still download it.
                  </div>
                )
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-gray-500">
                  No resume uploaded for this candidate.
                </div>
              )}
              {(detailsLoading || (resumeUrl && resumeLoading)) && (
                <div className="absolute inset-0 bg-white/90 p-4">
                  <div className="h-full w-full rounded-lg border border-gray-200 bg-white p-4">
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <SkeletonBlock className="h-6 col-span-2" />
                      <SkeletonBlock className="h-6 col-span-1" />
                    </div>
                    <SkeletonBlock className="h-4 w-5/6 mb-2" />
                    <SkeletonBlock className="h-4 w-2/3 mb-4" />
                    <div className="space-y-2">
                      {Array.from({ length: 14 }).map((_, index) => (
                        <SkeletonBlock key={`resume-line-skeleton-${index}`} className="h-3 w-full" />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : !candidateDetailsOnly && tab === 'score' ? (
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pb-6 pr-1">
            {detailsLoading ? (
              <div className="space-y-4">
                <SkeletonBlock className="h-16 w-full" />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div className="rounded-xl border border-gray-200 p-4 space-y-3">
                    <SkeletonBlock className="h-4 w-24" />
                    <SkeletonBlock className="h-10 w-28" />
                    <SkeletonBlock className="h-3 w-full" />
                    <SkeletonBlock className="h-3 w-11/12" />
                    <SkeletonBlock className="h-3 w-10/12" />
                    <SkeletonBlock className="h-3 w-9/12" />
                  </div>
                  <div className="rounded-xl border border-gray-200 p-4 space-y-3">
                    <SkeletonBlock className="h-5 w-40" />
                    {Array.from({ length: 5 }).map((_, idx) => (
                      <div key={`score-breakdown-skeleton-${idx}`} className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <SkeletonBlock className="h-3 w-28" />
                          <SkeletonBlock className="h-3 w-10" />
                        </div>
                        <SkeletonBlock className="h-2.5 w-full rounded-full" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <>
            {/* Deal-breaker banner removed per request */}

            {/* Resume extraction failed — uploaded file could not be read, so no AI score exists.
                Explain formally and point the recruiter at the Resume tab to preview the file. */}
            {normalized.isResumeExtractionFailed && (
              <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100">
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-amber-900">Resume could not be read</h3>
                    <p className="mt-1 text-sm leading-relaxed text-amber-800">
                      The candidate&apos;s uploaded resume appears to be low quality, empty, or unreadable,
                      so an automated match score could not be generated. The original file is available
                      in the <span className="font-semibold">Resume</span> tab for manual review.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Top row: Match score + AI (left) and Score breakdown (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="space-y-3">
                <div className="relative overflow-hidden bg-gradient-to-br from-[#007BFF] via-[#0056D2] to-[#003F9E] rounded-xl p-5 text-white shadow-lg min-h-[300px] flex flex-col">
                  <div className="absolute top-0 right-0 w-20 h-20 bg-white opacity-10 rounded-full -mr-10 -mt-10" />
                  <div className="relative">
                    {/* Candidate identity belongs in the primary narrative card */}
                    <div className="-mx-5 -mt-5 mb-3 border-b border-white border-opacity-20 bg-black/50 rounded-t-xl px-5 pt-5 pb-3">
                      <div className="flex items-stretch justify-between gap-4">
                        <div className="min-w-0 flex-1 flex items-center">
                          <div className="text-[20px] sm:text-[22px] font-extrabold leading-snug break-words">
                            <span className="inline-block">
                              {displayName || 'N/A'}
                            </span>
                          </div>
                        </div>

                        {normalized.score !== undefined && normalized.score !== null && (
                          <div className="flex-shrink-0 text-right flex items-center">
                            <div className="relative">
                              <div className="absolute -top-3 right-0 text-[11px] font-semibold text-indigo-100 uppercase tracking-wide whitespace-nowrap">
                                Match Score
                              </div>
                              <div className="text-3xl sm:text-4xl font-extrabold leading-none text-white">
                                {normalized.score}%
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* V2: Show scoringSummary as the primary narrative */}
                    {normalized.isV2 && normalized.scoringSummary ? (
                      <div>
                        <div className="flex items-center gap-1.5 mb-2">
                          <div className="w-5 h-5 rounded bg-white bg-opacity-20 flex items-center justify-center">
                            <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                          </div>
                          <h3 className="text-sm font-semibold text-white">AI Summary</h3>
                        </div>
                        {(() => {
                          const paras = formatAiSummaryParagraphs(normalized.scoringSummary);
                          const PREVIEW_PARAS = 3;
                          const shouldCollapse = paras.length > PREVIEW_PARAS;
                          const visible =
                            aiSummaryExpanded || !shouldCollapse ? paras : paras.slice(0, PREVIEW_PARAS);
                          return (
                            <>
                              <div className="text-xs sm:text-sm text-indigo-50 leading-relaxed space-y-2">
                                {visible.map((para, idx) => (
                                  <p key={`ai-summary-${idx}`} className="text-indigo-50">
                                    {para}
                                  </p>
                                ))}
                              </div>
                              {shouldCollapse && (
                                <button
                                  type="button"
                                  onClick={() => setAiSummaryExpanded((v) => !v)}
                                  className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-white/90 hover:text-white underline underline-offset-4"
                                >
                                  {aiSummaryExpanded ? 'Show less' : 'Show more'}
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    ) : normalized.aiAnalysis ? (
                      <div>
                        <div className="flex items-center gap-1.5 mb-2">
                          <div className="w-5 h-5 rounded bg-white bg-opacity-20 flex items-center justify-center">
                            <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                          </div>
                          <h3 className="text-sm font-semibold text-white">AI Analysis</h3>
                        </div>
                        <div className="text-xs sm:text-sm text-indigo-50 leading-relaxed space-y-2 pr-0.5">
                          {normalized.aiAnalysis.split('\n').map((line, idx) => {
                            const m = line.match(/^-\s*\*\*(.+?)\*\*:(.*)$/);
                            if (m) {
                              return (
                                <div key={idx} className="flex items-start gap-1.5">
                                  <span className="mt-0.5">&bull;</span>
                                  <span>
                                    <strong>{m[1].trim()}</strong>
                                    {m[2]}
                                  </span>
                                </div>
                              );
                            }
                            return (
                              <div key={idx} className="flex items-start gap-1.5">
                                <span className="mt-0.5">&bull;</span>
                                <span>{line.replace(/^-+\s*/, '')}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm sticky top-0">
                  {/* V2: Criteria-based breakdown */}
                  {normalized.isV2 && normalized.criteriaResults.length > 0 ? (
                    <>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">
                        Criteria Breakdown
                      </h4>
                      <div className="grid grid-cols-1 gap-2">
                        {normalized.criteriaResults.map((cr, i) => (
                          <CriterionResultCard key={cr.criterionId || i} criterion={cr} />
                        ))}
                      </div>
                    </>
                  ) : normalized.scoreBreakdown && Object.keys(normalized.scoreBreakdown).length > 0 ? (
                    <>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">
                        Score Breakdown
                      </h4>
                      <div className="grid grid-cols-1 gap-2.5">
                        {[
                          {
                            key: 'keyResponsibilitiesMatch',
                            label: 'Key Responsibilities',
                            max: 45,
                            emoji: '\uD83C\uDFAF',
                            bg: 'from-blue-500 to-blue-600',
                          },
                          {
                            key: 'experienceAndLevel',
                            label: 'Experience & Level',
                            max: 25,
                            emoji: '\u231B',
                            bg: 'from-indigo-500 to-indigo-600',
                          },
                          {
                            key: 'impactAchievements',
                            label: 'Impact & Achievements',
                            max: 15,
                            emoji: '\uD83D\uDCAA',
                            bg: 'from-green-500 to-green-600',
                          },
                          {
                            key: 'careerTrajectory',
                            label: 'Career Trajectory',
                            max: 5,
                            emoji: '\uD83D\uDCC8',
                            bg: 'from-pink-500 to-pink-600',
                          },
                          {
                            key: 'educationCredentials',
                            label: 'Education & Credentials',
                            max: 10,
                            emoji: '\uD83C\uDF93',
                            bg: 'from-teal-500 to-teal-600',
                          },
                        ].map(({ key, label, max, emoji, bg }) => {
                          const val = normalized.scoreBreakdown[key];
                          if (val === undefined) return null;
                          return (
                            <ScoreParameterCard
                              key={key}
                              paramKey={key}
                              label={label}
                              max={max}
                              emoji={emoji}
                              bgClass={bg}
                              value={val}
                            />
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <div className="w-10 h-10 mx-auto mb-1.5 rounded-full bg-gray-100 flex items-center justify-center">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <p className="text-xs text-gray-600">Score breakdown not available yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
              </>
            )}
          </div>
        ) : candidateDetailsOnly || tab === 'candidate' ? (
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pb-6 pr-1">
            {detailsLoading ? (
              <div className="space-y-3">
                <SkeletonBlock className="h-16 w-full" />
                <div className="rounded-xl border border-gray-200 p-4 space-y-3">
                  <SkeletonBlock className="h-4 w-40" />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Array.from({ length: 8 }).map((_, idx) => (
                      <div key={`candidate-field-skeleton-${idx}`} className="space-y-1.5">
                        <SkeletonBlock className="h-3 w-16" />
                        <SkeletonBlock className="h-4 w-full" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <>
            <div className="space-y-3">
              <div className="rounded-xl border border-indigo-100 bg-gradient-to-r from-indigo-50/70 via-blue-50/50 to-white p-4 shadow-sm">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                  <div className="mt-1 text-lg font-semibold text-gray-900 break-words">
                      {displayName || 'N/A'}
                    </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Professional Details</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
                  <div>
                    <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Current Role</label>
                    {renderField('currentRole', normalized.currentRole)}
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Experience</label>
                    {renderField('experienceYears', normalized.experienceYears ?? normalized.experienceYears ?? '', 'N/A', 'number')}
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Company</label>
                    {renderField('company', normalized.company)}
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Industry</label>
                    {renderField('industry', normalized.industry || (industries.length ? industries.join(', ') : ''))}
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Age</label>
                    {renderField('ageYears', normalized.ageYears ?? normalized.age, 'N/A', 'number')}
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Education</label>
                    {renderField('education', normalized.education)}
                  </div>
                  {/* <div>
                    <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Professional Qualification</label>
                    {renderField('professionalQualification', normalized.professionalQualification)}
                  </div> */}
                  {/* <div>
                    <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Work Authorization</label>
                    {renderField('workAuthorization', workAuthorizationDisplay || normalized.workAuthorization)}
                  </div> */}
                </div>
              </div>

              {normalized.storeId && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-1.5">
                    <BuildingOfficeIcon className="h-5 w-5 text-indigo-600 shrink-0" />
                    <span>Store Details</span>
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
                    <div>
                      <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Store Code</label>
                      <span className="text-xs text-slate-850 font-semibold block">{normalized.storeId}</span>
                    </div>
                    {normalized.storeDetails?.name && (
                      <div>
                        <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Store Name</label>
                        <span className="text-xs text-slate-850 font-medium block">{normalized.storeDetails.name}</span>
                      </div>
                    )}
                    {normalized.storeDetails?.category && (
                      <div>
                        <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Category</label>
                        <span className="text-xs text-slate-850 font-medium block">{normalized.storeDetails.category}</span>
                      </div>
                    )}
                    {(normalized.storeDetails?.city || normalized.storeDetails?.state) && (
                      <div>
                        <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Location</label>
                        <span className="text-xs text-slate-850 font-medium block">
                          {normalized.storeDetails.city && normalized.storeDetails.state
                            ? `${normalized.storeDetails.city}, ${normalized.storeDetails.state}`
                            : normalized.storeDetails.city || normalized.storeDetails.state}
                        </span>
                      </div>
                    )}
                    {normalized.storeDetails?.region && (
                      <div>
                        <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Region</label>
                        <span className="text-xs text-slate-850 font-medium block">{normalized.storeDetails.region}</span>
                      </div>
                    )}
                    {normalized.storeDetails?.armName && (
                      <div>
                        <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">ARM Name</label>
                        <span className="text-xs text-slate-850 font-medium block">{normalized.storeDetails.armName}</span>
                      </div>
                    )}
                    {normalized.storeDetails?.armEmail && (
                      <div>
                        <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">ARM Email</label>
                        <span className="text-xs text-slate-850 font-medium block">{normalized.storeDetails.armEmail}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Compensation & Availability</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
                  <div>
                    <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Current CTC (LPA)</label>
                    {renderField('currentCtcLacs', currentCtcFieldValue, 'N/A', 'number')}
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Expected CTC (LPA)</label>
                    {renderField('expectedCtcLacs', expectedCtcFieldValue, 'N/A', 'number')}
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Notice Period</label>
                    {renderField('noticePeriod', noticePeriodDisplayValue, 'N/A', 'text', noticePeriodEditValue)}
                  </div>
                  {/* <div>
                    <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Remote Preference</label>
                    {renderField('remoteWorkPreference', remoteWorkPreferenceDisplay || normalized.remoteWorkPreference)}
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Preferred Location</label>
                    {renderField('preferredLocation', normalized.preferredLocation)}
                  </div> */}
                  <div>
                    <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Email</label>
                    {renderField('email', normalized.email, 'N/A', 'email')}
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Phone</label>
                    {renderField('phone', normalized.phone, 'N/A', 'tel')}
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Location</label>
                    {renderField('location', normalized.location)}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Links, Skills & Background</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div>
                      <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">LinkedIn</label>
                      {editable ? (
                        renderField('linkedinUrl', normalized.linkedinUrl)
                      ) : normalized.linkedinUrl ? (
                        <a href={normalized.linkedinUrl} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:text-indigo-700 font-medium break-all">
                          {normalized.linkedinUrl}
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400 italic">N/A</span>
                      )}
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">GitHub</label>
                      {editable ? (
                        renderField('githubUrl', normalized.githubUrl)
                      ) : normalized.githubUrl ? (
                        <a href={normalized.githubUrl} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:text-indigo-700 font-medium break-all">
                          {normalized.githubUrl}
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400 italic">N/A</span>
                      )}
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Portfolio</label>
                      {editable ? (
                        renderField('portfolioUrl', normalized.portfolioUrl)
                      ) : normalized.portfolioUrl ? (
                        <a href={normalized.portfolioUrl} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:text-indigo-700 font-medium break-all">
                          {normalized.portfolioUrl}
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400 italic">N/A</span>
                      )}
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Behance</label>
                      {editable ? (
                        renderField('behanceUrl', normalized.behanceUrl)
                      ) : normalized.behanceUrl ? (
                        <a href={normalized.behanceUrl} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:text-indigo-700 font-medium break-all">
                          {normalized.behanceUrl}
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400 italic">N/A</span>
                      )}
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Website</label>
                      {editable ? (
                        renderField('websiteUrl', normalized.websiteUrl)
                      ) : normalized.websiteUrl ? (
                        <a href={normalized.websiteUrl} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:text-indigo-700 font-medium break-all">
                          {normalized.websiteUrl}
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400 italic">N/A</span>
                      )}
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Other Profile Links</label>
                      {extraProfileLinks.length ? (
                        <div className="flex flex-col gap-1">
                          {extraProfileLinks.slice(0, 5).map((link, idx) => {
                            const label = String(link?.label || '').trim();
                            const domain = String(link?.domain || '').trim();
                            const url = String(link?.url || '').trim();
                            const display = label || domain || `Link ${idx + 1}`;
                            return (
                              <a
                                key={`${url}-${idx}`}
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium break-all"
                              >
                                {display}
                              </a>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">N/A</span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="text-[10px] font-medium text-gray-400 mb-1 block uppercase tracking-wide">Skills</label>
                      {skills.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {skills.slice(0, 12).map((skill) => (
                            <span key={skill} className="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-0.5 text-[11px] font-medium">
                              {skill}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">N/A</span>
                      )}
                    </div>

                    <div>
                      <label className="text-[10px] font-medium text-gray-400 mb-1 block uppercase tracking-wide">Languages</label>
                      <span className={`text-xs ${languages.length ? 'text-gray-700 font-medium' : 'text-gray-400 italic'}`}>
                        {languages.length ? languages.join(', ') : 'N/A'}
                      </span>
                    </div>

                    <div>
                      <label className="text-[10px] font-medium text-gray-400 mb-1 block uppercase tracking-wide">Certifications</label>
                      <span className={`text-xs ${certifications.length ? 'text-gray-700 font-medium' : 'text-gray-400 italic'}`}>
                        {certifications.length ? certifications.join(', ') : 'N/A'}
                      </span>
                    </div>

                    <div>
                      <label className="text-[10px] font-medium text-gray-400 mb-1 block uppercase tracking-wide">Previous Companies</label>
                      <span className={`text-xs ${previousCompanies.length ? 'text-gray-700 font-medium' : 'text-gray-400 italic'}`}>
                        {previousCompanies.length ? previousCompanies.join(', ') : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Screening Metadata</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
                  <div>
                    <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Applied At</label>
                    <span className={`text-xs ${formatMetaDate(normalized.appliedAt) ? 'text-gray-700 font-medium' : 'text-gray-400 italic'}`}>
                      {formatMetaDate(normalized.appliedAt) || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Selected At</label>
                    <span className={`text-xs ${formatMetaDate(normalized.shortlistedAt) ? 'text-gray-700 font-medium' : 'text-gray-400 italic'}`}>
                      {formatMetaDate(normalized.shortlistedAt) || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Contacted At</label>
                    <span className={`text-xs ${formatMetaDate(normalized.contactedAt) ? 'text-gray-700 font-medium' : 'text-gray-400 italic'}`}>
                      {formatMetaDate(normalized.contactedAt) || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-gray-400 mb-0.5 block uppercase tracking-wide">Resume Parsing</label>
                    <span className={`text-xs ${normalized.resumeParsingStatus ? 'text-gray-700 font-medium capitalize' : 'text-gray-400 italic'}`}>
                      {normalized.resumeParsingStatus ? String(normalized.resumeParsingStatus).replace(/_/g, ' ') : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
              </>
            )}
          </div>
        ) : null}
      </div>

      <div className="flex shrink-0 justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-3">
        <button
          type="button"
          onClick={handleClose}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
        >
          Close
        </button>
      </div>
    </AnimatedModal>
  );
}

export default CandidateInsightModal;
